import type { UserContextPayload } from '@brightdrop/feature-flags-client';
import {
  buildUserContext as buildFromPackage,
  USER_CONTEXT_KIND,
} from '@brightdrop/feature-flags-client';
import type { Role } from '@gm-commercial/profile-model';

import { ROLE_KEYS } from '~/common/constants/common.constant';

/** Re-export so callers use the same constant as the package (multi-context key). */
export { USER_CONTEXT_KIND };

/**
 * LaunchDarkly context used before sign-in when `deferInitialization` is enabled.
 * Allows flag evaluation on login without an anonymous-then-identify double fetch.
 */
export const PRE_AUTH_LD_CONTEXT = {
  kind: 'user',
  key: 'pre-auth',
  anonymous: true,
} as const;

export type { UserContextPayload } from '@brightdrop/feature-flags-client';

/** Minimal profile shape needed to build feature-flag user context. */
export type ProfileForUserContext = {
  id?: string;
  role?: Role;
  organization?: {
    id: string;
    countryOfOperation?: UserContextPayload['countryOfOperation'];
  };
  hub?: { id: string };
  fleet?: { id: string };
  userPreferences?: { locale?: string };
};

/**
 * Builds the user context payload from fleet-management profile for setContext.
 * Delegates to the shared package builder.
 *
 * @param profile - Profile with id, role, org/hub/fleet, and organization country
 * @returns Payload for setContext(USER_CONTEXT_KIND, payload), or null if profile/id missing
 */
export function buildUserContext(
  profile: ProfileForUserContext | undefined | null
): UserContextPayload | null {
  if (!profile?.id) return null;
  const roleKey = profile.role
    ? String(ROLE_KEYS[profile.role] ?? '')
    : undefined;
  return buildFromPackage({
    id: profile.id,
    role: roleKey || undefined,
    organizationId: profile.organization?.id,
    hubId: profile.hub?.id,
    fleetId: profile.fleet?.id,
    language: profile.userPreferences?.locale,
    countryOfOperation: profile?.organization?.countryOfOperation,
  });
}
